import React, { useEffect, useState } from 'react';
import styles from './AnimatedWrapper.module.css';

interface AnimatedWrapperProps {
  isVisible: boolean;
  animation?: 'slideUp' | 'fadeIn'; // Extend as needed
  duration?: string; // e.g., '0.3s'
  easing?: string;
  children: React.ReactNode;
}

const DEFAULT_DURATION = '0.3s';

const AnimatedWrapper: React.FC<AnimatedWrapperProps> = ({
  isVisible,
  animation = 'fadeIn',
  duration = DEFAULT_DURATION,
  easing = 'ease-in-out',
  children,
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setIsExiting(false);
    } else {
      setIsExiting(true);
      const timeout = setTimeout(
        () => {
          setShouldRender(false);
          setIsExiting(false);
        },
        parseFloat(duration) * 1000
      );
      return () => clearTimeout(timeout);
    }
  }, [isVisible, duration]);

  if (!shouldRender) return null;

  const animationClassMap: Record<string, { in: string; out: string }> = {
    slideUp: {
      in: styles.animatedSlideUp,
      out: styles.slideDownOut,
    },
    fadeIn: {
      in: styles.animatedFadeIn,
      out: styles.fadeOut,
    },
  };

  const currentAnimation = animationClassMap[animation];
  const className = isExiting ? currentAnimation.out : currentAnimation.in;

  return (
    <div
      className={className}
      style={{
        animationDuration: duration,
        animationTimingFunction: easing,
        animationFillMode: 'both',
      }}
    >
      {children}
    </div>
  );
};

export default AnimatedWrapper;
